import FilterDown from "@NA/Icons/FilterDown";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    HasProduct,
    HasProductContext,
    withProduct,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { ResolveThunks, connect } from "react-redux";
import translate from "@insite/client-framework/Translate";
import CustomAddToList from "@NA/Components/CustomAddToList";
import { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import getColor from "@insite/mobius/utilities/getColor";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import pdpSaveForLater from "@NA/Services/ADIPdpSaveForLaterHandler";
import { css } from "styled-components";
import ADIColors from "@NA/Utils/ADIColors";
import { isPriceAvailable } from "@NA/Utils/Utility";
import { useRef } from "react";

type Props = HasProduct &
    WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    addToWishList,
    loadWishLists,
    pdpSaveForLater,
};

const mapStateToProps = (state: CustomApplicationState) => {
    let wishlistId = "";
    const wishListsDataView = getWishListsDataView(state, undefined).value;
    const wishlistIsLoading = getWishListsDataView(state, undefined).isLoading;
    const filteredWishList = wishListsDataView?.filter(item => item.properties.isSavedItemList === "1");
    if (filteredWishList && filteredWishList?.length > 0) {
        wishlistId = filteredWishList[0].id;
    }
    const productSettings = getSettingsCollection(state).productSettings;
    const isAuthenticated = state.context.session!.isAuthenticated;
    const adiUserRoles = state.ADIGlobalData.ADIUserRoleStore;
    return {
        wishlistId,
        filteredWishList,
        wishListsDataView,
        productSettings,
        isAuthenticated,
        wishlistIsLoading,
        showADIAddToList: (adiUserRoles.fullAccess && adiUserRoles.accounting) || adiUserRoles.fullAccess,
    };
};

export interface ProductDetailsAddToListStyles {
    productPrice?: ProductPriceStyles;
    pricingMainWrapper?: InjectableCss;
}

export interface ProductDetailsExpandedOptionStyles {
    addToListWrapper?: InjectableCss;
    addToListText?: TypographyProps;
    SavedWrapper?: InjectableCss;
    leftLine?: InjectableCss;
    rightLine?: InjectableCss;
    saveForLaterLink?: LinkPresentationProps;
    dataSheets?: InjectableCss;
    mainWrapper?: InjectableCss;
}

export const actionsStyles: ProductDetailsExpandedOptionStyles = {
    addToListWrapper: {
        css: css`
            position: relative;
            display: inline-block;
        `,
    },
    addToListText: {
        as: "span",
        css: css`
            color: ${ADIColors.darkBlue1};
            font-weight: 600;
            font-size: ${getProp("theme.typography.headerSecondary.size")};
            cursor: pointer;
            position: relative;
            svg {
                width: 10px;
                height: 7px;
                margin-left: 8px;
            }
        `,
    },
    SavedWrapper: {
        css: css`
            position: relative;
            color: ${ADIColors.darkBlue1};
            font-size: 14px;
            font-weight: 600 !important;
        `,
    },
    leftLine: {
        css: css`
            position: relative;
            &::before {
                content: "|";
                color: ${ADIColors.gray2};
                width: 4px;
                height: 19px;
                margin: 0 5px;
                font-size: 14px;
                font-weight: normal;
            }
        `,
    },
    rightLine: {
        css: css`
            position: relative;
            &::after {
                content: "|";
                color: ${ADIColors.gray2};
                width: 4px;
                height: 19px;
                margin: 0 5px;
                font-size: 14px;
                font-weight: normal;
            }
        `,
    },
    mainWrapper: {
        css: css`
            display: flex !important;
            justify-content: center;
            align-items: baseline !important;
            border-top: 1px solid ${ADIColors.gray2};
            max-width: 100%;
            position: relative;
            margin: 20px 0 15px;
        `,
    },
    saveForLaterLink: {
        css: css`
            color: ${ADIColors.darkBlue1};
            font-weight: 600;
            font-size: ${getProp("theme.typography.headerSecondary.size")};
            cursor: pointer;
            span {
                color: ${ADIColors.darkBlue1} !important;
                font-weight: 600;
                &:hover {
                    text-decoration: underline;
                }
            }
        `,
    },
    dataSheets: {
        css: css`
            font-size: ${getProp("theme.typography.headerSecondary.size")};
            line-height: normal !important;
            font-weight: 600 !important;
            cursor: pointer;
            color: ${ADIColors.darkBlue1} !important;
            &:hover {
                text-decoration: underline;
            }
        `,
    },
};

const styles = actionsStyles;

const ProductDetailsExpandedOption: React.FC<Props> = ({
    product,
    productContext,
    wishlistId,
    filteredWishList,
    isAuthenticated,
    loadWishLists,
    pdpSaveForLater,
    wishlistIsLoading,
    showADIAddToList,
}) => {
    const pricingIsAvailable = isPriceAvailable(
        productContext.productInfo.pricing,
        productContext.productInfo.qtyOrdered,
    );
    const componentRef = React.useRef<HTMLDivElement>(null);
    const [selectedProduct, setSelectedProduct] = React.useState<string>("");
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [isItemSaved, setIsItemSaved] = React.useState<boolean>(false);
    const [loadWishListsData, setLoadWishListsData] = React.useState<any>(filteredWishList);

    const addToListOpen = (id: string) => {
        setSelectedProduct(id);
        setIsOpen(prev => !prev);
    };

    const handleScrollToDocs = (elem: string) => {
        let targetElement = document.querySelector(`[data-test-selector="${elem}"]`);
        let offsetSize = document.querySelector<HTMLElement>(`[data-test-selector="${elem}"]`)?.offsetTop;

        if (targetElement) {
            let start = window.scrollY;
            let target = targetElement.getBoundingClientRect().top + window.scrollY - 230;
            if (window.innerWidth > 1024) {
                let duration = 800;
                let startTime: number = 0;

                function animateScroll(currentTime: number) {
                    if (!startTime) startTime = currentTime;
                    let progress = currentTime - startTime;
                    let easeInOutQuad = (progress: number) =>
                        progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;

                    window.scrollTo(0, start + (target - start) * easeInOutQuad(progress / duration));

                    if (progress < duration) {
                        requestAnimationFrame(animateScroll);
                    }
                }
                requestAnimationFrame(animateScroll);
            } else if (window.innerWidth < 1024 && window.innerWidth >= 768) {
                target = 150;
                window.scrollTo(0, offsetSize! - target);
            } else {
                window.scrollTo(0, offsetSize!);
            }
        }
    };
    const hasFetchedWishList = useRef(false);

    React.useEffect(() => {
        if (!hasFetchedWishList.current && showADIAddToList && isAuthenticated && !wishlistIsLoading) {
            hasFetchedWishList.current = true;
            loadWishLists({});
        }
    }, []);

    React.useEffect(() => {
        if (wishlistId) {
            setLoadWishListsData(filteredWishList);
        }
    }, [filteredWishList]);

    if (!product) {
        return null;
    }

    const saveForLaterHandler = async () => {
        const wishlist = await loadWishListsData?.[0];
        if (!wishlist) return;

        setIsLoading(true);
        try {
            await pdpSaveForLater({
                wishListId: wishlist.id,
                productId: product.id,
                qtyOrdered: Number(product.properties.minimuM_QTY),
                unitOfMeasure: "EA",
                properties: { name: "SAVEDITEM@#$" },
                cartLineId: "",
                parameter: null,
                name: "SAVEDITEM@#$",
            });
            setIsItemSaved(true);
        } catch {
            console.error("Save for later failed error message");
        }
        setIsLoading(false);
    };

    return (
        <>
            {isAuthenticated && showADIAddToList && pricingIsAvailable ? (
                <StyledWrapper {...styles.mainWrapper}>
                    <StyledWrapper {...styles.addToListWrapper} ref={componentRef}>
                        <Typography {...styles.addToListText} onClick={() => addToListOpen(product.productNumber)}>
                            {translate("Add To List")}
                            <FilterDown />
                        </Typography>

                        {isOpen && selectedProduct === product.productNumber ? (
                            <CustomAddToList
                                isOpen={isOpen}
                                productContext={productContext}
                                setIsOpen={setIsOpen}
                                sourceName="Product Details"
                            />
                        ) : null}
                    </StyledWrapper>

                    <StyledWrapper {...styles.SavedWrapper}>
                        {isItemSaved ? (
                            <>
                                <Typography {...styles.leftLine}></Typography>
                                {translate("Item Saved")}
                            </>
                        ) : (
                            <Link {...styles.saveForLaterLink} onClick={saveForLaterHandler}>
                                <Typography {...styles.leftLine}></Typography>
                                {translate("Save for Later")}
                                {isLoading && (
                                    <LoadingSpinner size={15} style={{ position: "absolute", right: "50%" }} />
                                )}
                            </Link>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.dataSheets}>
                        {product.documents && product.documents.length > 0 ? (
                            <>
                                <Typography {...styles.leftLine}></Typography>
                                <Typography onClick={() => handleScrollToDocs("DocumentsTitle")}>
                                    {translate("Data Sheets")}
                                </Typography>
                            </>
                        ) : null}
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <></>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withProduct(withProductContext(ProductDetailsExpandedOption))),
    definition: {
        displayName: "ADI Expanded Option",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
